import styled from 'styled-components'
import { Box, Icon, Text } from '../../../core-components'
import { ThemeColors, ThemeShadows } from '../../../theme'

export type BaseButtonVariant = 'major' | 'minor' | 'cautionHigh' | 'cautionLow'

export const BaseButtonIcon = styled(Icon)``
export const BaseButtonEndIcon = styled(Icon)``

/** Internal design system component for sharing styles between Button and OptionButton */
export const BaseButtonBox = styled(Box)<{
  endIconColor?: ThemeColors
  endIconFocusColor?: ThemeColors
  endIconHoverColor?: ThemeColors
  focusBg?: ThemeColors
  focusColor?: ThemeColors
  focusShadow?: ThemeShadows
  hoverBg: ThemeColors
  hoverColor?: ThemeColors
  iconColor: ThemeColors
  iconFocusColor?: ThemeColors
  iconHoverColor: ThemeColors
}>`
  transition: background-color 0.1s ease-in-out, color 0.1s ease-in-out;
  cursor: pointer;
  text-decoration: none;

  ${BaseButtonIcon} {
    color: ${({ iconColor, theme }) => theme.colors[iconColor]};
  }

  ${BaseButtonEndIcon} {
    color: ${({ endIconColor, theme }) => endIconColor && theme.colors[endIconColor]};
  }

  :hover,
  :disabled,
  &[aria-disabled='true'] {
    ::after {
      box-shadow: none;
    }
  }

  :hover,
  &[aria-expanded='true'] {
    background: ${({ hoverBg, theme }) => theme.colors[hoverBg]};

    ${Text} {
      color: ${({ hoverColor, theme }) => hoverColor && theme.colors[hoverColor]};
    }

    ${BaseButtonIcon} {
      color: ${({ iconHoverColor, theme }) => theme.colors[iconHoverColor]};
    }

    ${BaseButtonEndIcon} {
      color: ${({ endIconHoverColor, theme }) =>
        endIconHoverColor && theme.colors[endIconHoverColor]};
    }
  }

  :focus {
    outline: none;
    // We can't move it to ::after shadow hack (see Box) because not all buttons have shadows
    box-shadow: ${({ theme, focusShadow }) => focusShadow && theme.shadows[focusShadow]} !important;
    background: ${({ focusBg, theme }) => focusBg && theme.colors[focusBg]};

    ::after {
      box-shadow: ${({ focusShadow }) => focusShadow && 'none'};
    }

    ${Text} {
      color: ${({ focusColor, theme }) => focusColor && theme.colors[focusColor]};
    }

    ${BaseButtonIcon} {
      color: ${({ iconFocusColor, theme }) => iconFocusColor && theme.colors[iconFocusColor]};
    }

    ${BaseButtonEndIcon} {
      color: ${({ endIconFocusColor, theme }) =>
        endIconFocusColor && theme.colors[endIconFocusColor]};
    }
  }

  :disabled,
  &[aria-disabled='true'] {
    cursor: default;
    background: ${({ theme }) => theme.colors['bg/disabled']};

    ${Text} {
      color: ${({ theme }) => theme.colors['text/action-disabled']};
    }

    ${BaseButtonIcon}, ${BaseButtonEndIcon} {
      color: ${({ theme }) => theme.colors['icon/disabled']};
    }
  }
`

export const baseButtonVariantProps: Record<
  BaseButtonVariant,
  {
    bg: ThemeColors
    border?: ThemeShadows
    color: ThemeColors
    hoverBg: ThemeColors
    hoverColor: ThemeColors
    iconColor: ThemeColors
    iconHoverColor: ThemeColors
  }
> = {
  major: {
    bg: 'bg/button-major',
    color: 'text/action-oncolor',
    hoverBg: 'bg/button-hovered-major',
    hoverColor: 'text/action-oncolor',
    iconColor: 'icon/on-color',
    iconHoverColor: 'icon/on-color',
  },
  minor: {
    bg: 'bg/button-minor',
    border: 'border/on-minor',
    color: 'text/action-minor',
    hoverBg: 'bg/button-hovered-minor',
    hoverColor: 'text/action-minor',
    iconColor: 'icon/action-minor',
    iconHoverColor: 'icon/action-minor',
  },
  cautionHigh: {
    bg: 'bg/button-caution',
    color: 'text/action-major-oncolor',
    hoverBg: 'bg/button-hovered-caution',
    hoverColor: 'text/action-oncolor',
    iconColor: 'icon/on-color',
    iconHoverColor: 'icon/on-color',
  },
  cautionLow: {
    bg: 'bg/button-minor',
    border: 'border/on-minor',
    color: 'text/action-caution',
    hoverBg: 'bg/button-hovered-caution',
    hoverColor: 'text/action-oncolor',
    iconColor: 'icon/action-caution',
    iconHoverColor: 'icon/on-color',
  },
}

export const baseButtonProps = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  m: 0,
  border: 'none',
  borderRadius: 10,
  px: 4,
  py: 3,
  w: 'fit-content',
} as const
